import { usePersistedJsonState } from '@superfunnel/react-hooks';
import Badge from '@superfunnel/ui-core/v2/Badge';
import Button from '@superfunnel/ui-core/v2/Button';
import { FastArrowLeft, NavArrowRight } from 'iconoir-react';
import { cloneElement, ReactNode, useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../contexts/AuthContext';
import { useWhitelabel } from '../../contexts/WhitelabelContext';
import JumpToAccountModal from '../../modals/JumpToAccountModal';
import { classNames } from '../../utils';
import SidebarSupportCard from './SidebarSupportCard';

export interface SidebarItem {
  name: string;
  href: string;
  icon: any;
  current: boolean;
  count?: number;
  badge?: string;
  special?: boolean;
}

export interface SidebarTeamItem {
  id: string;
  name: string;
  current: boolean;
  initial: string;
  href: string;
}

interface Props {
  navigation: SidebarItem[];
  teams: SidebarTeamItem[];
  collapsed?: boolean;
  onCollapseChange: (collapsed: boolean) => void;
}

const teamsColorsMap = [
  {
    icon: 'bg-[#E5E7FF]',
    block: 'text-[#4C1D95] hover:bg-[#E5E7FF80]',
    active: 'bg-[#E5E7FF80]',
    ring: 'ring-[#E5E7FF]',
  },
  {
    icon: 'bg-[#D1FAE5]',
    block: 'text-[#065F46] hover:bg-[#D1FAE580]',
    active: 'bg-[#D1FAE580]',
    ring: 'ring-[#D1FAE5]',
  },
  {
    icon: 'bg-[#FEF3C7]',
    block: 'text-[#92400E] hover:bg-[#FEF3C780]',
    active: 'bg-[#FEF3C780]',
    ring: 'ring-[#FEF3C7]',
  },
  {
    icon: 'bg-[#DBEAFE]',
    block: 'text-[#1E3A8A] hover:bg-[#DBEAFE80]',
    active: 'bg-[#DBEAFE80]',
    ring: 'ring-[#DBEAFE]',
  },
  {
    icon: 'bg-[#FCE7F3]',
    block: 'text-[#BE185D] hover:bg-[#FCE7F380]',
    active: 'bg-[#FCE7F380]',
    ring: 'ring-[#FCE7F3]',
  },
];

function AnimatedBorderButton({
  onClick,
  collapsed,
  children,
  current,
}: {
  onClick: () => void;
  collapsed?: boolean;
  children: ReactNode;
  current?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      className={classNames(
        'rounded-2lg group relative flex h-9 w-full transform items-center gap-x-3 whitespace-nowrap px-3 text-sm font-medium leading-6 transition-all duration-300 ease-in-out',
        'text-primary',
        collapsed && 'px-2',
      )}
      style={{
        background: current
          ? 'linear-gradient(to bottom, rgba(231,222,255,0.12) -8.1%, rgba(255,255,255,0.08) 25.11%, rgba(122,96,255,0.12) 101.84%)'
          : 'linear-gradient(to bottom, rgba(231,222,255,0.04) -8.1%, rgba(255,255,255,0.04) 25.11%, rgba(122,96,255,0.04) 101.84%)',
      }}
    >
      {/* Animated border overlay */}
      <span
        className="rounded-2lg pointer-events-none absolute inset-0"
        style={{
          padding: '1px',
          background:
            'linear-gradient(90deg, rgba(209,194,255,0.3), rgba(209,194,255,0.6), rgba(122,96,255,0.4), rgba(209,194,255,0.3))',
          backgroundSize: '200% 100%',
          animation: 'gradient-border 6s ease-in-out infinite',
          WebkitMask:
            'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
          WebkitMaskComposite: 'xor',
          maskComposite: 'exclude',
        }}
      />
      <style>{`
        @keyframes gradient-border {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 200% 50%; }
        }
      `}</style>
      {/* Content with z-index to be above the backgrounds */}
      <span className="relative z-10 flex items-center gap-x-3">
        {children}
      </span>
    </button>
  );
}

export default function DashboardSidebar({
  navigation,
  teams,
  collapsed,
  onCollapseChange,
}: Props) {
  const navigate = useNavigate();
  const { firebaseUser, isSupportAdmin } = useContext(AuthContext);
  const { appName, logoFull, logoIcon } = useWhitelabel();
  const [showAllTeams, _setShowAllTeams] = useState(false);
  const [showJumpToModal, setShowJumpToModal] = useState(false);
  const [hideAccountManagement, setHideAccountManagement] =
    usePersistedJsonState('HIDE_ACCOUNT_MANAGEMENT', false);

  return (
    <>
      {isSupportAdmin ? (
        <JumpToAccountModal
          open={showJumpToModal}
          onClose={() => setShowJumpToModal(false)}
          onSelect={(account) => {
            navigate(`/workspace/${account.data().slug}`);
            setShowJumpToModal(false);
          }}
        />
      ) : (
        <></>
      )}
      <div
        className={classNames(
          'border-base-200 flex grow flex-col gap-y-5 overflow-y-auto overflow-x-hidden border-r bg-white transition-all duration-300 ease-in-out',
          collapsed ? 'px-5' : 'px-5',
        )}
      >
        <div
          className={classNames(
            'flex h-16 shrink-0 items-center',
            collapsed ? 'justify-center' : 'justify-between',
          )}
        >
          <img
            className={classNames(
              'mt-5 h-7 w-auto transform cursor-pointer object-cover object-left transition-all duration-300 ease-in-out',
              collapsed ? 'hidden' : 'block',
            )}
            src={logoFull}
            alt={appName}
            onClick={() => navigate('/workspaces')}
          />
          <img
            className={classNames(
              'mt-5 h-12 w-auto transform cursor-pointer object-contain transition-all duration-300 ease-in-out',
              collapsed ? 'block' : 'hidden',
            )}
            src={logoIcon}
            alt={appName}
            onClick={() => navigate('/workspaces')}
          />
        </div>
        <div className="bg-base-200 h-px w-full"></div>
        <nav className="flex flex-1 flex-col">
          <ul role="list" className="flex w-full flex-1 flex-col gap-y-5">
            <li className="space-y-1">
              {navigation.map((item) => (
                <li key={item.name} className="active:outline-hidden">
                  {item.special ? (
                    <AnimatedBorderButton
                      onClick={() => navigate(item.href)}
                      collapsed={collapsed}
                      current={item.current}
                    >
                      {cloneElement(item.icon, {
                        'className': classNames(
                          item.icon.props.className,
                          'text-base-500 h-5 w-5 shrink-0',
                        ),
                        'aria-hidden': 'true',
                      })}
                      <span
                        className={classNames(
                          'transform-duration-300 transform transition-opacity ease-in-out',
                          collapsed ? 'opacity-0' : 'opacity-100',
                        )}
                      >
                        {item.name}
                      </span>
                    </AnimatedBorderButton>
                  ) : (
                    <button
                      onClick={() => navigate(item.href)}
                      className={classNames(
                        item.current
                          ? 'bg-base-100 text-primary font-medium'
                          : 'text-primary hover:bg-base-100 hover:text-primary font-normal',
                        'rounded-2lg group flex h-9 w-full transform items-center gap-x-3 whitespace-nowrap px-3 text-sm leading-6 transition-all duration-300 ease-in-out',
                        collapsed && 'px-2',
                      )}
                    >
                      {cloneElement(item.icon, {
                        'className': classNames(
                          item.icon.props.className,
                          item.current
                            ? 'text-base-500'
                            : 'text-base-500 group-hover:text-base-500',
                          'h-5 w-5 shrink-0',
                        ),
                        'aria-hidden': 'true',
                      })}
                      <span
                        className={classNames(
                          'transform-duration-300 transform transition-opacity ease-in-out',
                          collapsed ? 'opacity-0' : 'opacity-100',
                        )}
                      >
                        {item.name}
                      </span>
                      {item.badge && !collapsed ? (
                        <div>
                          <Badge size="xs" rounded="full">
                            {item.badge}
                          </Badge>
                        </div>
                      ) : null}
                      {item.count ? (
                        <span
                          className="ml-auto w-9 min-w-max whitespace-nowrap rounded-full bg-indigo-600 px-2.5 py-0.5 text-center text-xs font-medium leading-5 text-white ring-1 ring-inset ring-indigo-500"
                          aria-hidden="true"
                        >
                          {item.count}
                        </span>
                      ) : null}
                    </button>
                  )}
                  {/* </Tooltip> */}
                </li>
              ))}
            </li>
            <div className="bg-base-200 h-px w-full"></div>
            <li className="space-y-1">
              <ul role="list" className="space-y-1">
                {(hideAccountManagement
                  ? teams.slice(0, 1)
                  : showAllTeams
                    ? teams
                    : teams.slice(0, 5)
                ).map((team, index) => (
                  <li key={team.id || team.name}>
                    <button
                      className={classNames(
                        'group flex w-full transform items-center gap-x-2 rounded-lg px-2.5 py-1.5 text-sm leading-6 transition-all duration-300 ease-in-out',
                        collapsed && 'w-auto px-1.5',
                        !collapsed && teamsColorsMap[index].block,
                        !collapsed &&
                          team.current &&
                          teamsColorsMap[index].active,
                      )}
                      onClick={() => {
                        navigate(team.href);
                      }}
                    >
                      <span
                        className={classNames(
                          'flex h-6 w-6 shrink-0 transform items-center justify-center text-xs font-medium transition-all duration-300 ease-in-out',
                          collapsed
                            ? 'rounded-md ring-offset-1 hover:ring-1'
                            : 'rounded-lg',
                          teamsColorsMap[index].ring,
                          team.current && 'ring-1',
                          teamsColorsMap[index].icon,
                        )}
                      >
                        {team.initial}
                      </span>
                      <span
                        className={classNames(
                          'transform-duration-300 transform truncate transition-opacity ease-in-out',
                          collapsed ? 'hidden' : 'inline',
                        )}
                      >
                        {team.name}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
              {isSupportAdmin ? (
                <div
                  className={classNames(
                    'mt-1 space-y-1 whitespace-nowrap',
                    collapsed && 'hidden',
                  )}
                >
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-base-500 text-xs"
                    onClick={() => {
                      setShowJumpToModal(true);
                    }}
                  >
                    Jump to...
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-base-500 text-xs opacity-0"
                    onClick={() => {
                      setHideAccountManagement(!hideAccountManagement);
                    }}
                  >
                    {hideAccountManagement ? 'Show' : 'Hide'}
                  </Button>
                </div>
              ) : (
                <></>
              )}
              {/* {!showAllTeams && (teams?.length || 0) > 3 ? (
                <div className="-mx-2 mt-1 space-y-1 whitespace-nowrap">
                  <button
                    className={classNames(
                      'text-indigo-200 hover:bg-indigo-700 hover:text-white',
                      'group flex w-full gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold transition-opacity',
                      collapsed ? 'opacity-0' : 'opacity-100',
                    )}
                    onClick={() => {
                      setShowAllTeams(true);
                    }}
                  >
                    <span className="truncate">Show all</span>
                  </button>
                </div>
              ) : (
                <></>
              )} */}
            </li>
            <div className="flex-1"></div>
            <div
              className={classNames(
                'flex justify-center',
                collapsed ? 'px-0' : 'px-0',
              )}
            >
              <SidebarSupportCard collapsed={collapsed} />
            </div>
            <div
              className={classNames(
                'font-sm hover:bg-base-100 flex cursor-pointer content-center items-center gap-2.5 rounded-lg px-2 py-2 text-sm',
                collapsed && 'px-2',
              )}
              onClick={() => {
                onCollapseChange(!collapsed);
              }}
            >
              <FastArrowLeft
                className={classNames(
                  'text-base-500 h-5 w-5 shrink-0 font-normal transition-all duration-300 ease-in-out',
                  collapsed && 'rotate-180',
                )}
                aria-hidden="true"
              />
              <span className={collapsed ? 'hidden' : 'whitespace-nowrap'}>
                Collapse sidebar
              </span>
            </div>
            <div className="bg-base-200 h-px w-full"></div>
            <li className="py-6 pt-0">
              <button
                onClick={() => navigate('/workspaces')}
                className={classNames(
                  'flex w-full content-start items-center justify-start gap-3 text-sm font-semibold leading-6 text-white',
                  collapsed ? 'justify-center px-2' : '',
                )}
              >
                <img
                  className="bg-base-200 h-10 min-h-10 w-10 min-w-10 rounded-full"
                  src={firebaseUser?.photoURL ?? ''}
                  alt=""
                />
                <span className="sr-only">Your profile</span>
                <div
                  className={classNames(
                    'text-base-900 flex-1 flex-col items-start font-medium',
                    collapsed ? 'hidden' : 'flex',
                  )}
                >
                  <span>{firebaseUser?.displayName}</span>
                  <span className="text-base-500 text-xs">
                    {firebaseUser?.email && firebaseUser.email.length > 19
                      ? `${firebaseUser.email.substring(0, 19)}...`
                      : firebaseUser?.email}
                  </span>
                </div>
                {!collapsed && (
                  <NavArrowRight className="text-base-500 h-5 w-5 shrink-0" />
                )}
              </button>
            </li>
          </ul>
        </nav>
      </div>
    </>
  );
}
